import {Component} from '@angular/core';
import {FormArray, FormControl, FormGroup} from '@angular/forms';
import {MaterialModule} from '../../../material.module';

import {
  Webinar,
  WebinarChatMessage,
  WebinarEngagementItem,
  WebinarRegistration,
  WebinarSeries,
} from '../../../models/webinar.model';
import {DecoratedWebinar, StatusFilter, WebinarStatus} from '../webinars-admin.types';
import {WebinarsDetailComponent} from '../webinars-detail/webinars-detail.component';
import {WebinarsListComponent} from '../webinars-list/webinars-list.component';

type WebinarsFixtureState =
  | 'list-load-failure'
  | 'series-partial-failure'
  | 'scheduled-engagements'
  | 'scheduled-engagements-ended';

@Component({
  selector: 'app-webinars-visual-fixture',
  standalone: true,
  imports: [MaterialModule, WebinarsListComponent, WebinarsDetailComponent],
  templateUrl: './webinars-visual-fixture.component.html',
  host: {class: 'webinars-m3 webinars-shell'},
})
export class WebinarsVisualFixtureComponent {
  readonly fixtureState: WebinarsFixtureState = this.resolveFixtureState();
  readonly series: WebinarSeries[] = [];
  readonly seriesLoadErrorMessage = 'Recurring webinar series could not be loaded.';
  readonly listLoadErrorMessage = 'Could not load webinars. Retry to fetch them again.';
  readonly statusMeta: Record<WebinarStatus, {label: string; icon: string}> = {
    live: {label: 'Live now', icon: 'fiber_manual_record'},
    upcoming: {label: 'Upcoming', icon: 'schedule'},
    draft: {label: 'Draft', icon: 'pending'},
    past: {label: 'Completed', icon: 'history'},
  };

  readonly registrationColumns = ['name', 'email', 'registeredAt', 'source'];
  readonly registrations: WebinarRegistration[] = [
    {
      id: 'fixture-registration-1',
      webinarId: 'fixture-webinar-scheduled-engagements',
      clientId: 'fixture-client',
      name: 'Alex Olsen',
      email: 'alex@example.com',
      source: 'landing-page',
      registeredAt: '2099-07-01T08:14:00.000Z',
    },
  ];
  readonly chatMessages: WebinarChatMessage[] = [
    {
      id: 'fixture-chat-1',
      type: 'chat',
      text: 'Will the slides be shared after the webinar?',
      ts: Date.parse('2099-07-01T09:05:00.000Z'),
      user: {id: 'user-1', name: 'Alex Olsen'},
    },
  ];
  readonly detailForm = this.createDetailForm();
  readonly selectedWebinar = this.buildSelectedWebinar();
  readonly engagementItems = this.buildEngagementItems();

  private readonly allWebinars: DecoratedWebinar[] = [
    {
      id: 'fixture-webinar-upcoming',
      clientId: 'fixture-client',
      type: 'webinar',
      title: 'Quarterly product update',
      slug: 'quarterly-product-update',
      companyName: 'Intuvio',
      provider: 'youtube',
      videoCode: 'fixture-video-1',
      scheduledAt: '2099-07-01T09:00:00.000Z',
      endAt: '2099-07-01T10:00:00.000Z',
      liveViewers: 0,
      status: 'upcoming',
    },
    {
      id: 'fixture-webinar-draft',
      clientId: 'fixture-client',
      type: 'webinar',
      title: 'Partner launch dry run',
      slug: 'partner-launch-dry-run',
      companyName: 'Intuvio',
      provider: 'vimeo',
      videoCode: 'fixture-video-2',
      scheduledAt: null,
      endAt: null,
      liveViewers: 0,
      status: 'draft',
    },
    {
      id: 'fixture-webinar-past',
      clientId: 'fixture-client',
      type: 'webinar',
      title: 'Spring customer briefing',
      slug: 'spring-customer-briefing',
      companyName: 'Intuvio',
      provider: 'dacast',
      videoCode: 'fixture-video-3',
      scheduledAt: '2026-03-18T13:00:00.000Z',
      endAt: '2026-03-18T14:00:00.000Z',
      liveViewers: 0,
      status: 'past',
    },
  ];

  webinars: DecoratedWebinar[] = [...this.allWebinars];
  filterTerm = '';
  statusFilter: StatusFilter[] = [];
  statusCounts: Record<WebinarStatus, number> = {
    live: 0,
    upcoming: 1,
    draft: 1,
    past: 1,
  };

  get showDetailFixture(): boolean {
    return this.fixtureState === 'scheduled-engagements' || this.fixtureState === 'scheduled-engagements-ended';
  }

  get isListLoadFailureFixture(): boolean {
    return this.fixtureState === 'list-load-failure';
  }

  onSearchTerm(value: string): void {
    this.filterTerm = value ?? '';
    this.applyFilters();
  }

  setStatusFilter(value: StatusFilter[]): void {
    this.statusFilter = Array.isArray(value) ? value : [];
    this.applyFilters();
  }

  refresh(): void {
    if (this.isListLoadFailureFixture) {
      return;
    }
    this.filterTerm = '';
    this.statusFilter = [];
    this.webinars = [...this.allWebinars];
  }

  openWebinar(): void {}

  openSeries(): void {}

  createWebinar(): void {}

  private resolveFixtureState(): WebinarsFixtureState {
    if (typeof window === 'undefined') return 'series-partial-failure';
    const value = window.location.search
      ? new URLSearchParams(window.location.search).get('webinarsVisualFixture')
      : null;
    if (value === 'scheduled-engagements' || value === 'scheduled-engagements-ended') {
      return value;
    }
    if (value === 'list-load-failure') {
      return value;
    }
    return 'series-partial-failure';
  }

  private createDetailForm(): FormGroup {
    return new FormGroup({
      title: new FormControl('Quarterly product update'),
      companyName: new FormControl('Intuvio'),
      adminEmail: new FormControl('events@intuvio.com'),
      speakerBadgeLabel: new FormControl('Speaker'),
      speakers: new FormArray([]),
      registrationPageUrl: new FormControl('https://example.com/register'),
      accessMode: new FormControl('register'),
      showOnListing: new FormControl(true),
      hostPassword: new FormControl(''),
      slug: new FormControl('quarterly-product-update'),
      provider: new FormControl('youtube'),
      videoCode: new FormControl('fixture-video-1'),
      scheduledDate: new FormControl(new Date(2099, 6, 1)),
      scheduledTime: new FormControl('11:00'),
      endDate: new FormControl(new Date(2099, 6, 1)),
      endTime: new FormControl('12:00'),
      showViewerCount: new FormControl(true),
      showElapsedTime: new FormControl(true),
      webinarKind: new FormControl('one_time'),
      timezone: new FormControl('Europe/Oslo'),
      recurrenceFrequency: new FormControl('weekly'),
      recurrenceInterval: new FormControl(1),
      recurrenceDays: new FormGroup({
        SU: new FormControl(false),
        MO: new FormControl(false),
        TU: new FormControl(false),
        WE: new FormControl(false),
        TH: new FormControl(true),
        FR: new FormControl(false),
        SA: new FormControl(false),
      }),
      recurrenceStartsOn: new FormControl(new Date(2099, 6, 1)),
      recurrenceLocalStartTime: new FormControl('11:00'),
      recurrenceDurationMinutes: new FormControl(60),
      recurrenceEndCondition: new FormControl('count'),
      recurrenceCount: new FormControl(4),
      recurrenceEndsOn: new FormControl(new Date(2099, 6, 22)),
    });
  }

  private buildSelectedWebinar(): Webinar {
    if (this.fixtureState === 'scheduled-engagements-ended') {
      return {
        id: 'fixture-webinar-scheduled-engagements-ended',
        clientId: 'fixture-client',
        title: 'Quarterly product update',
        slug: 'quarterly-product-update',
        companyName: 'Intuvio',
        provider: 'youtube',
        videoCode: 'fixture-video-1',
        scheduledAt: '2026-03-18T13:00:00.000Z',
        endAt: '2026-03-18T14:00:00.000Z',
      };
    }

    return {
      id: 'fixture-webinar-scheduled-engagements',
      clientId: 'fixture-client',
      title: 'Quarterly product update',
      slug: 'quarterly-product-update',
      companyName: 'Intuvio',
      provider: 'youtube',
      videoCode: 'fixture-video-1',
      scheduledAt: '2099-07-01T09:00:00.000Z',
      endAt: '2099-07-01T10:00:00.000Z',
    };
  }

  private buildEngagementItems(): WebinarEngagementItem[] {
    if (this.fixtureState === 'scheduled-engagements-ended') {
      return [
        {
          id: 'fixture-ended-scheduled',
          clientId: 'fixture-client',
          webinarId: this.selectedWebinar.id,
          type: 'message',
          status: 'scheduled',
          createdAt: '2026-03-18T12:20:00.000Z',
          scheduledFor: '2026-03-18T13:30:00.000Z',
          scheduleLocalDate: '2026-03-18',
          scheduleLocalTime: '14:30',
          timezone: 'Europe/Oslo',
          message: {text: 'Thanks for joining. The follow-up email is on its way.'},
        },
        {
          id: 'fixture-ended-expired',
          clientId: 'fixture-client',
          webinarId: this.selectedWebinar.id,
          type: 'message',
          status: 'expired',
          createdAt: '2026-03-18T12:00:00.000Z',
          expiredAt: '2026-03-18T14:05:00.000Z',
          expiredReason: 'webinar_ended',
          scheduledFor: '2026-03-18T13:50:00.000Z',
          scheduleLocalDate: '2026-03-18',
          scheduleLocalTime: '14:50',
          timezone: 'Europe/Oslo',
          message: {text: 'Please download the worksheet before you leave.'},
        },
      ];
    }

    return [
      {
        id: 'fixture-scheduled-message',
        clientId: 'fixture-client',
        webinarId: this.selectedWebinar.id,
        type: 'message',
        status: 'scheduled',
        createdAt: '2099-07-01T08:30:00.000Z',
        scheduledFor: '2099-07-01T09:15:00.000Z',
        scheduleLocalDate: '2099-07-01',
        scheduleLocalTime: '11:15',
        timezone: 'Europe/Oslo',
        message: {text: 'Slides are here: https://example.com/slides'},
      },
      {
        id: 'fixture-draft-poll',
        clientId: 'fixture-client',
        webinarId: this.selectedWebinar.id,
        type: 'poll',
        status: 'draft',
        createdAt: '2099-07-01T08:35:00.000Z',
        poll: {
          question: 'Which feature should we demo next?',
          allowMultiple: false,
          resultsVisibility: 'attendees',
          showResponseCount: true,
          options: [
            {id: 'feature-a', label: 'Workflow automation'},
            {id: 'feature-b', label: 'Lead scoring'},
          ],
        },
        responses: [],
      },
      {
        id: 'fixture-posted-resource',
        clientId: 'fixture-client',
        webinarId: this.selectedWebinar.id,
        type: 'resource',
        status: 'posted',
        createdAt: '2099-07-01T08:00:00.000Z',
        postedAt: '2099-07-01T09:02:00.000Z',
        resource: {
          title: 'Pricing deck',
          url: 'https://example.com/pricing-deck.pdf',
          description: 'Current regional pricing and packaging.',
          fileType: 'PDF',
          buttonLabel: 'Open deck',
        },
        downloads: [
          {
            id: 'fixture-download-1',
            userId: 'user-1',
            name: 'Alex Olsen',
            email: 'alex@example.com',
            downloadedAt: '2099-07-01T09:04:00.000Z',
          },
        ],
      },
      {
        id: 'fixture-cancelled-message',
        clientId: 'fixture-client',
        webinarId: this.selectedWebinar.id,
        type: 'message',
        status: 'cancelled',
        createdAt: '2099-07-01T08:10:00.000Z',
        cancelledAt: '2099-07-01T08:55:00.000Z',
        scheduledFor: '2099-07-01T09:45:00.000Z',
        scheduleLocalDate: '2099-07-01',
        scheduleLocalTime: '11:45',
        timezone: 'Europe/Oslo',
        message: {text: 'This reminder was cancelled before publish.'},
      },
      {
        id: 'fixture-expired-message',
        clientId: 'fixture-client',
        webinarId: this.selectedWebinar.id,
        type: 'message',
        status: 'expired',
        createdAt: '2099-07-01T07:55:00.000Z',
        expiredAt: '2099-07-01T10:01:00.000Z',
        expiredReason: 'webinar_ended',
        scheduledFor: '2099-07-01T10:00:00.000Z',
        scheduleLocalDate: '2099-07-01',
        scheduleLocalTime: '12:00',
        timezone: 'Europe/Oslo',
        message: {text: 'This scheduled note expired when the webinar ended.'},
      },
    ];
  }

  private applyFilters(): void {
    const search = this.filterTerm.trim().toLowerCase();
    const selected = new Set<StatusFilter>(this.statusFilter ?? []);

    this.webinars = this.allWebinars.filter((item) => {
      if (selected.size && !selected.has(item.status)) {
        return false;
      }

      if (!search) {
        return true;
      }

      return item.title.toLowerCase().includes(search) || item.slug.toLowerCase().includes(search);
    });
  }
}
